/**
 * Base AI Provider
 * 
 * Abstract base class that provides common functionality for all AI providers.
 * Specific providers (OpenAI, Anthropic, Google, xAI) extend this class.
 */

import { invoke } from '../../shims/forge-bridge';
import {
  AIProvider,
  AIModelConfig,
  AIMessage,
  AIRequestOptions,
  AIResponse,
  IAIProvider,
  AIProviderError
} from './AIProvider.types';

/**
 * Abstract base class for AI providers
 * Provides common functionality and defines the contract for provider implementations
 */
export abstract class BaseAIProvider implements IAIProvider {
  abstract readonly provider: AIProvider;
  
  /**
   * Get the authentication token for this provider
   * Each provider must implement this with their specific token retrieval
   */
  protected abstract getAuthToken(): Promise<string>;
  
  /**
   * Generate a completion - must be implemented by each provider
   */
  abstract generateCompletion(
    messages: AIMessage[],
    options: AIRequestOptions
  ): Promise<AIResponse>;
  
  /**
   * Check if the provider is available - must be implemented by each provider
   */
  abstract isAvailable(): Promise<boolean>;
  
  /**
   * Get available models - must be implemented by each provider
   */
  abstract getAvailableModels(): AIModelConfig[];
  
  /**
   * Normalize messages to remove any provider-specific formatting
   * Can be overridden by providers that need custom normalization
   */
  protected normalizeMessages(messages: AIMessage[]): AIMessage[] {
    return messages.map(msg => ({
      role: msg.role,
      content: msg.content.trim()
    }));
  }
  
  /**
   * Create a standardized provider error
   */
  protected createProviderError(
    code: string,
    message: string,
    originalError?: unknown
  ): AIProviderError {
    return {
      provider: this.provider,
      code,
      message,
      originalError
    };
  }
  
  /**
   * Handle and transform errors from provider APIs
   * Can be overridden for provider-specific error handling
   */
  protected handleError(error: unknown): never {
    if (error instanceof Error) {
      const providerError = this.createProviderError(
        'PROVIDER_ERROR',
        error.message,
        error
      );
      throw providerError;
    }
    
    throw this.createProviderError(
      'UNKNOWN_ERROR',
      'An unknown error occurred',
      error
    );
  }
  
  /**
   * Validate request options before making a request
   */
  protected validateRequestOptions(options: AIRequestOptions): void {
    if (!options.model) {
      throw this.createProviderError(
        'INVALID_OPTIONS',
        'Model is required in request options'
      );
    }
    
    if (options.temperature !== undefined && (options.temperature < 0 || options.temperature > 2)) {
      throw this.createProviderError(
        'INVALID_OPTIONS',
        'Temperature must be between 0 and 2'
      );
    }
  }
  
  /**
   * Invoke a Forge resolver function
   * Wrapper around invoke for consistent error handling
   */
  protected async invokeResolver<T>(functionName: string): Promise<T> {
    try {
      const result = await invoke(functionName);
      
      // Handle case where invoke returns an object wrapper
      if (result && typeof result === 'object') {
        // Check common wrapper patterns
        const obj = result as Record<string, unknown>;
        if ('payload' in obj) return obj.payload as T;
        if ('value' in obj) return obj.value as T;
        if ('data' in obj) return obj.data as T;
        if ('result' in obj) return obj.result as T;
      }
      
      return result as T;
    } catch (error) {
      throw this.createProviderError(
        'RESOLVER_ERROR',
        `Failed to invoke resolver: ${functionName}`,
        error
      );
    }
  }
}
